import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as atlas from "azure-maps-control";
import * as azureMapsDrawingTools from "azure-maps-drawing-tools";
import "azure-maps-drawing-tools/dist/atlas-drawing.min.css";
import clsx from "clsx";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaCheck } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import {
    customerPolgyonQueryOptions,
    parcelQueryOptions,
    useUpdateCustomerPolygon,
} from "src/api/customer-service-query-options";
import useAccountEnhanced from "../../../hooks/use-account-enhanced";

const MapComponent = ({
    customerCode,
    address,
    coordinates,
}: {
    customerCode: string;
    address: string;
    coordinates: { lat: number; lng: number };
}) => {
    const queryClient = useQueryClient();

    const customerPolygonQuery = useQuery(
        customerPolgyonQueryOptions({ customerCode }),
    );

    const updateCustomerPolygonMutation = useUpdateCustomerPolygon({
        customerCode,
    });

    const accountEnhanced = useAccountEnhanced();
    const mapRef = useRef<atlas.Map | null>(null);
    const dataSourceRef = useRef<atlas.source.DataSource | null>(null);
    const drawingManagerRef =
        useRef<azureMapsDrawingTools.drawing.DrawingManager | null>(null);
    const [geofenceEditMode, setGeofenceEditMode] = useState<
        "address" | "coordinate" | "draw" | null
    >(null);
    const [parcelLoading, setParcelLoading] = useState(false);
    const [parcelGeoJson, setParcelGeoJson] =
        useState<atlas.data.FeatureCollection | null>(null);

    // Drawing
    const [drawingStatus, setDrawingStatus] = useState<
        "no-polygon" | "multiple-polygon" | "valid" | null
    >(null);
    const [mapReady, setMapReady] = useState(false);

    useEffect(() => {
        const geoJson = customerPolygonQuery.data?.GeoJson
            ? JSON.parse(customerPolygonQuery.data?.GeoJson)
            : null;

        if (geoJson && dataSourceRef.current && mapRef.current && mapReady) {
            dataSourceRef.current.clear();
            dataSourceRef.current.add(geoJson);

            const bounds = atlas.data.BoundingBox.fromData(geoJson);

            mapRef.current.setCamera({
                bounds: bounds,
                padding: 20,
            });
        }

        if (mapRef.current && mapReady) {
            mapRef.current.markers.add(
                new atlas.HtmlMarker({
                    position: [coordinates.lng, coordinates.lat],
                }),
            );
        }

        if (!geoJson && mapRef.current && mapReady) {
            mapRef.current.setCamera({
                center: [coordinates.lng, coordinates.lat],
                zoom: 15,
            });
        }
    }, [customerPolygonQuery.data?.GeoJson, mapReady, coordinates]);

    useEffect(() => {
        function initializeMap() {
            const map = new atlas.Map("myMap", {
                language: "en-US",
                authOptions: {
                    authType: atlas.AuthenticationType.subscriptionKey,
                    subscriptionKey:
                        "2AOAX1DVcq5C809hqDSmyPU3gQ0wyvW8gNZu21lxwhQ",
                },
                style: "satellite",
            });

            mapRef.current = map;

            map.events.add("ready", () => {
                setupMap();
                setMapReady(true);
            });
        }

        function setupMap() {
            const dataSource = new atlas.source.DataSource();

            mapRef.current!.sources.add(dataSource);
            dataSourceRef.current = dataSource;

            const polygonLayer = new atlas.layer.PolygonLayer(
                dataSource,
                undefined,
                {
                    fillColor: "rgba(255,0,0,0.5)", // Red color with 50% opacity
                },
            );

            mapRef.current!.layers.add(polygonLayer);

            setupDrawingManager();
        }

        function setupDrawingManager() {
            drawingManagerRef.current =
                new azureMapsDrawingTools.drawing.DrawingManager(
                    mapRef.current!,
                    {
                        toolbar: undefined,
                    },
                );

            setupDrawingManagerLayers();
            setupDrawingManagerEvents();
        }

        function setupDrawingManagerLayers() {
            const layers = drawingManagerRef.current!.getLayers();
            const previewLayers = drawingManagerRef.current!.getPreviewLayers();

            layers.polygonLayer!.setOptions({
                fillColor: "red",
                fillOpacity: 0.3,
            });
            layers.polygonOutlineLayer!.setOptions({ strokeColor: "red" });
            layers.lineLayer!.setOptions({
                strokeColor: "red",
                strokeWidth: 4,
            });
            previewLayers.lineLayer!.setOptions({
                strokeColor: "red",
                strokeWidth: 4,
                strokeDashArray: [3, 3],
            });
        }

        function setupDrawingManagerEvents() {
            mapRef.current!.events.add(
                "drawingcomplete",
                drawingManagerRef.current!,
                () => {
                    const shapes = drawingManagerRef
                        .current!.getSource()
                        .getShapes();
                    setDrawingStatus(
                        shapes.length > 1 ? "multiple-polygon" : "valid",
                    );
                },
            );

            mapRef.current!.events.add(
                "drawingerased",
                drawingManagerRef.current!,
                () => {
                    const shapes = drawingManagerRef
                        .current!.getSource()
                        .getShapes();
                    setDrawingStatus(
                        shapes.length === 0 ? "no-polygon" : "valid",
                    );
                },
            );
        }

        if (!mapRef.current) {
            initializeMap();
        }
    }, []);

    async function handleGetParcel(
        coordinates: { lat: number; lng: number } | null,
        address: string | null,
    ) {
        if (dataSourceRef.current && mapRef.current) {
            const payload: {
                Latitude?: number;
                Longitude?: number;
                Address?: string;
            } = {};

            if (coordinates) {
                payload["Latitude"] = coordinates.lat;
                payload["Longitude"] = coordinates.lng;
            } else if (address) {
                payload["Address"] = address.trim();
            }

            setGeofenceEditMode(coordinates ? "coordinate" : "address");
            setParcelLoading(true);

            const parcel = await queryClient.fetchQuery(
                parcelQueryOptions({
                    latitude: payload.Latitude,
                    longitude: payload.Longitude,
                    address: payload.Address,
                }),
            );

            setParcelLoading(false);

            if (parcel === null) {
                setGeofenceEditMode(null);
                toast.error(
                    `No parcel found for ${
                        coordinates ? "coordinates" : "address"
                    }`,
                );
                return;
            }

            const geoJson = JSON.parse(parcel.GeoJson);

            setParcelGeoJson(geoJson);

            dataSourceRef.current.clear();
            dataSourceRef.current.add(geoJson);

            // Set the map view to fit the GeoJSON bounds
            const bounds = atlas.data.BoundingBox.fromData(geoJson);

            mapRef.current.setCamera({
                bounds: bounds,
                padding: 20,
            });
        }
    }

    function handleParcelCancelClick() {
        const geoJson = customerPolygonQuery.data?.GeoJson
            ? JSON.parse(customerPolygonQuery.data?.GeoJson)
            : null;

        dataSourceRef.current!.clear();
        setGeofenceEditMode(null);
        dataSourceRef.current!.add(geoJson);

        mapRef.current!.setCamera({
            bounds: atlas.data.BoundingBox.fromData(geoJson),
            padding: 20,
        });
    }

    async function handleParcelSaveClick() {
        if (parcelGeoJson && customerCode) {
            // TODO: Maybe optimistic update (with the parcelGeoJson, NOT the response of api) for the customerPolygonQuery
            await updateCustomerPolygonMutation.mutateAsync({
                customerCode: customerCode.trim(),
                parcelGeoJson: JSON.stringify(parcelGeoJson),
                employeeCode: accountEnhanced.me.employeeCode,
            });

            // setGeoJson(parcelGeoJson); // TODO: Update the geoJson state
            setGeofenceEditMode(null);
        }
    }

    // Drawing
    function handleDrawClick() {
        setGeofenceEditMode("draw");
        setDrawingStatus("no-polygon");

        if (dataSourceRef.current === null) {
            return;
        }

        if (drawingManagerRef.current === null) {
            return;
        }

        dataSourceRef.current.clear();
        drawingManagerRef.current.getSource().clear();

        drawingManagerRef.current.setOptions({
            mode: azureMapsDrawingTools.drawing.DrawingMode.drawPolygon,
            toolbar: new azureMapsDrawingTools.control.DrawingToolbar({
                buttons: ["draw-polygon", "erase-geometry"],
                containerId: "drawing-toolbar",
            }),
        });
    }

    function handleDrawingCancelClick() {
        setGeofenceEditMode(null);

        if (dataSourceRef.current === null) {
            return;
        }

        if (drawingManagerRef.current === null) {
            return;
        }

        drawingManagerRef.current.setOptions({
            mode: azureMapsDrawingTools.drawing.DrawingMode.idle,
            toolbar: undefined,
        });

        drawingManagerRef.current.getSource().clear();

        const geoJson = customerPolygonQuery.data?.GeoJson
            ? JSON.parse(customerPolygonQuery.data?.GeoJson)
            : null;

        if (geoJson) {
            dataSourceRef.current.add(geoJson);
        }
    }

    async function handleDrawingSaveClick() {
        if (drawingManagerRef.current === null) {
            alert("Drawing manager is not initialized");
            return;
        }

        if (dataSourceRef.current === null) {
            alert("Data source is not initialized");
            return;
        }

        if (customerCode === null) {
            alert("Customer code is not set");
            return;
        }

        const shapes = drawingManagerRef.current.getSource().getShapes();
        if (shapes.length !== 1) {
            return;
        }

        const [coordinates] =
            shapes[0].getCoordinates() as atlas.data.Position[][][];
        const polygon = new atlas.data.Polygon(coordinates);

        dataSourceRef.current.clear();
        dataSourceRef.current.add(polygon);
        drawingManagerRef.current.getSource().clear();

        drawingManagerRef.current.setOptions({
            mode: azureMapsDrawingTools.drawing.DrawingMode.idle,
            toolbar: undefined,
        });

        const feature = new atlas.data.Feature(
            polygon,
            shapes[0].getProperties(),
        );

        const featureCollection = new atlas.data.FeatureCollection([feature]);

        await updateCustomerPolygonMutation.mutateAsync({
            customerCode: customerCode.trim(),
            parcelGeoJson: JSON.stringify(featureCollection),
            employeeCode: accountEnhanced.me.employeeCode,
        });

        setGeofenceEditMode(null);
    }

    function handleMapStyleChange(e: ChangeEvent<HTMLSelectElement>) {
        const mapStyle = e.target.value;

        if (mapRef.current) {
            mapRef.current.setStyle({
                style: mapStyle,
            });
        }
    }

    return (
        <div className="h-full relative flex flex-col">
            <div className="card card-compact bg-gray-700 mb-4">
                <div className="card-body ">
                    <div className="grid grid-cols-3 gap-6">
                        <div>
                            <button
                                className="btn btn-secondary flex w-full"
                                onClick={() => handleGetParcel(null, address)}
                            >
                                <span>Address Search</span>
                                {geofenceEditMode === "address" &&
                                    parcelLoading && (
                                        <div className="loading loading-spinner loading-sm" />
                                    )}
                            </button>
                            {geofenceEditMode === "address" &&
                                !parcelLoading && (
                                    <div className="mt-1 space-x-1 flex">
                                        <button
                                            disabled={
                                                updateCustomerPolygonMutation.isPending
                                            }
                                            className="btn btn-success btn-sm h-6 min-h-6 flex-1"
                                            onClick={handleParcelSaveClick}
                                        >
                                            {updateCustomerPolygonMutation.isPending ? (
                                                <div className="loading loading-spinner loading-sm" />
                                            ) : (
                                                <FaCheck />
                                            )}
                                        </button>
                                        <button
                                            className="btn btn-error btn-sm h-6 min-h-6 flex-1"
                                            onClick={handleParcelCancelClick}
                                        >
                                            <MdCancel />
                                        </button>
                                    </div>
                                )}
                        </div>
                        <div>
                            <button
                                className="btn btn-secondary w-full"
                                onClick={() =>
                                    handleGetParcel(coordinates, null)
                                }
                            >
                                <span>Lat/Long Search</span>
                                {geofenceEditMode === "coordinate" &&
                                    parcelLoading && (
                                        <div className="loading loading-spinner loading-sm" />
                                    )}
                            </button>
                            {geofenceEditMode === "coordinate" &&
                                !parcelLoading && (
                                    <div className="mt-1 space-x-1 flex">
                                        <button
                                            className="btn btn-success btn-sm h-6 min-h-6 flex-1"
                                            onClick={handleParcelSaveClick}
                                            disabled={
                                                updateCustomerPolygonMutation.isPending
                                            }
                                        >
                                            {updateCustomerPolygonMutation.isPending ? (
                                                <div className="loading loading-spinner loading-sm" />
                                            ) : (
                                                <FaCheck />
                                            )}
                                        </button>
                                        <button
                                            className="btn btn-error btn-sm h-6 min-h-6 flex-1"
                                            onClick={handleParcelCancelClick}
                                        >
                                            <MdCancel />
                                        </button>
                                    </div>
                                )}
                        </div>
                        <div>
                            <button
                                className="btn btn-secondary w-full"
                                onClick={handleDrawClick}
                            >
                                Draw Geofence
                            </button>
                            {geofenceEditMode === "draw" && (
                                <div className="mt-1 space-x-1 flex">
                                    <button
                                        className="btn btn-success btn-sm h-6 min-h-6 flex-1"
                                        onClick={handleDrawingSaveClick}
                                        disabled={drawingStatus !== "valid"}
                                    >
                                        <FaCheck />
                                    </button>
                                    <button
                                        className="btn btn-error btn-sm h-6 min-h-6 flex-1"
                                        onClick={handleDrawingCancelClick}
                                    >
                                        <MdCancel />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="h-full relative">
                <div id="myMap" style={{ height: "100%", width: "100%" }}></div>
                <div id="drawing-toolbar" className="absolute top-0"></div>
                <div className="absolute top-0 right-0">
                    <select
                        className="select select-xs select-bordered"
                        onChange={handleMapStyleChange}
                    >
                        <option value="satellite">Satellite</option>
                        <option value="road">Road</option>
                    </select>
                </div>

                {drawingStatus === "multiple-polygon" && (
                    <div
                        role="alert"
                        className="alert alert-warning w-max absolute top-2 right-4"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="stroke-current shrink-0 h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                        <span>Error! Multiple polygons detected</span>
                    </div>
                )}
                <div
                    className={clsx(
                        "absolute bottom-0 left-0 px-2 py-1 text-sm font-semibold ",
                        {
                            "bg-green-400 text-green-950":
                                customerPolygonQuery.data?.SyncedToSkyBitz,
                            "bg-red-400 text-red-950":
                                !customerPolygonQuery.data?.SyncedToSkyBitz,
                        },
                    )}
                >
                    Skybitz Synced:{" "}
                    {customerPolygonQuery.data?.SyncedToSkyBitz ? "Yes" : "No"}
                </div>
            </div>
        </div>
    );
};

export default MapComponent;
